import React, { useState, useRef, useEffect, useCallback } from "react";
import "./index.m.css";
import { SUBSCRIPTION_PAGE_STRINGS } from "../../../../constants";

import { QuoteIcon } from "./icons";

export const TestimonialsSection = ({ data = {} }) => {
  const testimonials = data.testimonials || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);
  const interactionTimerRef = useRef(null);

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  const scrollTo = useCallback((index) => {
    if (!carouselRef.current || !carouselRef.current.children[index]) return;
    
    const container = carouselRef.current;
    const card = container.children[index];
    const containerCenter = container.offsetWidth / 2;
    const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
    const targetScrollLeft = cardCenter - containerCenter;

    container.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });
  }, []);

  const handleNext = useCallback(() => {
    if (!carouselRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    const children = carouselRef.current.children;
    const scrollAmount = (children.length > 1) 
      ? children[1].offsetLeft - children[0].offsetLeft 
      : clientWidth;
    
    if (scrollLeft + clientWidth >= scrollWidth - 10) {
      carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, []);

  const handlePrev = useCallback(() => {
    if (!carouselRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    const children = carouselRef.current.children;
    const scrollAmount = (children.length > 1) 
      ? children[1].offsetLeft - children[0].offsetLeft 
      : clientWidth;
    
    if (scrollLeft <= 10) {
      carouselRef.current.scrollTo({ left: scrollWidth, behavior: 'smooth' });
    } else {
      carouselRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      handleNext();
    }, 4000);
  }, [handleNext]);

  const stopAutoPlay = useCallback(() => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
  }, []);

  const handleInteraction = useCallback(() => {
    stopAutoPlay();
    if (interactionTimerRef.current) clearTimeout(interactionTimerRef.current);
    
    interactionTimerRef.current = setTimeout(() => {
      startAutoPlay();
    }, 4000);
  }, [startAutoPlay, stopAutoPlay]);

  const handleScroll = useCallback(() => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    
    const scrollLeft = container.scrollLeft;
    const clientWidth = container.clientWidth;
    const scrollWidth = container.scrollWidth;
    
    let closestIndex = 0;
    let minDistance = Infinity;
    
    const children = container.children;
    for (let i = 0; i < children.length; i++) {
      const card = children[i];
      const distance = Math.abs(card.offsetLeft - scrollLeft);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }
    
    // If scrolled to the very end (with a 2px tolerance for rounding issues)
    if (Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 2) {
      closestIndex = children.length - 1;
    }
    
    setActiveIndex(closestIndex);
    activeIndexRef.current = closestIndex;
  }, []);

  useEffect(() => {
    const container = carouselRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      // Call once initially to set the correct active index
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    startAutoPlay();
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      if (interactionTimerRef.current) clearTimeout(interactionTimerRef.current);
    };
  }, [startAutoPlay]);

  return (
    <section styleName="testimonialsSection" aria-label="Testimonials">
      <h2 styleName="sectionHeading">{SUBSCRIPTION_PAGE_STRINGS.DH.TESTIMONIALS_HEADING}</h2>
      
      <div 
        styleName="carouselWrapper"
        onMouseEnter={stopAutoPlay}
        onMouseLeave={startAutoPlay}
        onMouseDown={handleInteraction}
        onTouchStart={handleInteraction}
        onTouchMove={handleInteraction}
        onWheel={handleInteraction}
      >
        <button 
          styleName="navButton prevButton" 
          onClick={handlePrev}
          aria-label="Previous testimonial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
            <path d="M0.0175781 10.833L10.834 21.6484L12.7393 19.7432L3.83691 10.8418L3.82812 10.833L12.7383 1.92285L10.833 0.0175781L0.0175781 10.833Z" fill="#C78D3C" stroke="#F8E7D3" strokeWidth="0.025"/>
          </svg>
        </button>

        <div styleName="carouselContainer" ref={carouselRef}>
          {testimonials.map((t, idx) => (
            <article key={t.id} styleName="testimonialCard" data-index={idx}>
              <QuoteIcon />
              <p styleName="testimonialDesc">{t.description}</p>
              
              <div styleName="profileRow">
                <img 
                  src={t.profileImage} 
                  alt="" 
                  styleName="profileImage" 
                />
                <div styleName="profileInfo">
                  <div styleName="profileName">{t.name}</div>
                  <div styleName="profileOcc">{t.occupation}</div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <button 
          styleName="navButton nextButton" 
          onClick={handleNext}
          aria-label="Next testimonial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
            <path d="M12.7383 10.833L1.92188 21.6484L0.0166016 19.7432L8.91895 10.8418L8.92773 10.833L0.0175781 1.92285L1.92285 0.0175781L12.7383 10.833Z" fill="#C78D3C" stroke="#F8E7D3" strokeWidth="0.025"/>
          </svg>
        </button>
      </div>

      {testimonials.length > 1 && (
        <div styleName="indicators">
          {testimonials.map((_, idx) => (
            <button
              key={idx}
              styleName={`indicatorDot ${idx === activeIndex ? 'activeDot' : ''}`}
              onClick={() => scrollTo(idx)}
              aria-label={`Go to testimonial ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
